import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MailCheck, ArrowLeft } from "lucide-react";
import { BrandLogo } from "@/components/MarketingNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { apiError } from "@/lib/api";
import { HERO_BG } from "@/lib/brand";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email, origin_url: window.location.origin });
      setSent(true);
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12">
        <div className="mb-10"><BrandLogo /></div>
        <div className="max-w-sm w-full">
          {sent ? (
            <div data-testid="forgot-sent">
              <div className="h-12 w-12 rounded-full bg-blue-50 flex items-center justify-center"><MailCheck className="h-6 w-6 text-blue-600" /></div>
              <h1 className="font-display text-2xl font-bold text-slate-900 mt-5">Check your email</h1>
              <p className="text-slate-500 mt-2 text-sm">If an account exists for <span className="font-medium text-slate-700">{email}</span>, we've sent a link to reset your password. The link expires in 1 hour.</p>
              <p className="text-slate-400 mt-4 text-sm">Didn't get it? Check your spam folder or <button onClick={() => setSent(false)} className="text-blue-600 font-medium" data-testid="forgot-retry">try again</button>.</p>
              <Link to="/login" className="mt-8 inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-blue-600"><ArrowLeft className="h-4 w-4" /> Back to log in</Link>
            </div>
          ) : (
            <>
              <h1 className="font-display text-3xl font-bold text-slate-900">Forgot password?</h1>
              <p className="text-slate-500 mt-2">No problem. Enter your email and we'll send you a link to reset it.</p>
              <form onSubmit={submit} className="mt-8 space-y-4" data-testid="forgot-form">
                {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="forgot-error">{error}</div>}
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email" className="mt-1" />
                </div>
                <Button type="submit" disabled={loading} data-testid="forgot-submit" className="w-full bg-blue-600 hover:bg-blue-700 h-11">
                  {loading ? "Sending…" : "Send reset link"}
                </Button>
              </form>
              <Link to="/login" className="mt-6 inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-blue-600"><ArrowLeft className="h-4 w-4" /> Back to log in</Link>
            </>
          )}
        </div>
      </div>
      <div className="hidden lg:block relative">
        <img src={HERO_BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(22,48,92,0.85), rgba(37,99,235,0.55))" }} />
      </div>
    </div>
  );
};

export default ForgotPassword;
